import { IDevice, UAParser } from 'ua-parser-js';

import { uuidv4 } from 'helpers/utils';

import { DEFAULT_EMPTY_URL_PARAMS, SYSTEMS_NAMES } from '../constants';
import DataProvider from '../DataProvider';
import { EventDataTrack } from '../Interfaces/Events';
import { DeviceType, EventActionType, StringDictionary } from '../types';

// Google Search Appliance on iOS
const GOOGLE_SEARCH_SAFARI_REGEX = [/\bgsa\/([\w.]+) .*safari\//i];

const CUSTOM_BROWSERS_DETECTION = [
    GOOGLE_SEARCH_SAFARI_REGEX,
    [UAParser.BROWSER.VERSION, [UAParser.BROWSER.NAME, 'Safari Mobile']],
];

const UA_DEFAULT_NAME = 'unknown';
const UA_DEFAULT_VERSION = '0';

export default class Utils {
    public static uuidLocalStorageKey = 'uuid';
    public static releaseDateLocalStorageKey = 'release_date';
    public static uaParser: UAParser = new UAParser({ browser: CUSTOM_BROWSERS_DETECTION });

    public static getDefaultUrlParams = () => DEFAULT_EMPTY_URL_PARAMS;

    public static getUrlParams(): StringDictionary {
        const urlParamsFromStore = localStorage.getItem('urlParams');

        const urlParams = typeof urlParamsFromStore === 'string' && JSON.parse(urlParamsFromStore);
        const localUrlParams: StringDictionary = urlParams || this.getDefaultUrlParams();

        const params: StringDictionary = {};

        new URLSearchParams(window.location.search).forEach((value, key) => {
            params[key] = value;
        });

        try {
            return Object.keys(params).length ? params : localUrlParams;
        } catch (e) {
            return {
                data: 'empty_url',
                error: JSON.stringify(e),
            };
        }
    }

    public static getUuid(): string {
        const uuid = localStorage.getItem(this.uuidLocalStorageKey);

        if (uuid) {
            return uuid;
        }

        const newUuid = uuidv4();

        localStorage.setItem(this.uuidLocalStorageKey, newUuid);

        return newUuid;
    }

    public static setReleaseDate(releaseDate: unknown): void {
        if (typeof releaseDate === 'string' && releaseDate) {
            localStorage.setItem(this.releaseDateLocalStorageKey, releaseDate);
        }
    }

    public static getReleaseDate(): string | null {
        return localStorage.getItem(this.releaseDateLocalStorageKey) || 'default';
    }

    public static parseUserAgentDevice(userAgent: string): IDevice {
        return this.uaParser.setUA(userAgent).getDevice();
    }

    public static getCountry() {
        return localStorage.getItem('country') || null;
    }

    public static getCookies(): object {
        const reducer = (accumulator: StringDictionary, current: string) => {
            const [name, value] = current.split('=');

            accumulator[name] = value;

            return accumulator;
        };

        return document.cookie.split('; ').reduce(reducer, {});
    }

    public static getDeviceType(): DeviceType {
        const uaBrowserInfo = this.uaParser.setUA(navigator.userAgent);

        // Agree with analytic team, if uaParser can't detect device type set desktop
        return (uaBrowserInfo?.getDevice()?.type || 'desktop') as DeviceType;
    }

    public static getDeviceModel(): string | undefined {
        return this.uaParser.setUA(navigator.userAgent).getDevice()?.model;
    }

    public static getBrowserInfo(): object {
        const uaBrowserInfo = this.uaParser.setUA(navigator.userAgent);

        return {
            'os.name': uaBrowserInfo?.getOS()?.name || UA_DEFAULT_NAME,
            'os.version': uaBrowserInfo?.getOS()?.version || UA_DEFAULT_VERSION,
            'browser.name': uaBrowserInfo?.getBrowser()?.name || UA_DEFAULT_NAME,
            'browser.version': uaBrowserInfo?.getBrowser()?.version || UA_DEFAULT_VERSION,
        };
    }

    public static deleteObjectKeys = (normalizeExample: string[], payload: Record<string, any>) => {
        normalizeExample.forEach((key: string) => delete payload[key]);

        return payload;
    };

    public static cleanObject = (object: Record<string, any>): any => {
        for (const propName in object) {
            if (object[propName] === null || object[propName] === undefined) {
                delete object[propName];
            }
        }

        return object;
    };

    public static getNavigatorLanguage = () => {
        let language;

        if (navigator?.languages?.length) {
            language = navigator.languages[0];
        } else {
            language =
                navigator?.userLanguage || navigator.language || navigator?.browserLanguage || 'default_browser_lang';
        }

        return language;
    };

    public static normalizeEventLabelFormat = (data?: EventDataTrack | null): EventDataTrack => {
        if (data?.event_label) {
            if (typeof data.event_label === 'string') {
                return {
                    ...data,
                    event_label: Utils.normalizeLabel(data.event_label),
                };
            } else if (Array.isArray(data.event_label)) {
                const newEventLabel: Array<string> = [];

                data.event_label.forEach((element: string) => {
                    newEventLabel.push(Utils.normalizeLabel(element));
                });

                // avoid different event_label in analytics with the same values
                newEventLabel.sort();

                return {
                    ...data,
                    event_label: newEventLabel,
                };
            }
        }

        return <EventDataTrack>data;
    };

    static normalizeLabel = (value: string) =>
        value
            .replace(/\s+/g, '_')
            .replace(/-/g, '_')
            .replace(/[^\w\s-]/gi, '')
            .toLowerCase();

    public static getAnalyticData = async (eventName: string, eventAction?: EventActionType | null) => {
        const utmSource = DataProvider.getUTMSource();
        const userSource = DataProvider.getUserSource();

        return {
            event_type: eventName,
            host: window.location.hostname,
            url: window.location.pathname,
            screen_height: window.screen.height,
            screen_width: window.screen.width,
            lang: localStorage.getItem('language') || 'default_lang',
            language: Utils.getNavigatorLanguage(),
            agent: navigator.userAgent,
            device: Utils.getDeviceModel(),
            referrer: document.referrer,
            timestamp: Date.now(),
            browser_info: Utils.getBrowserInfo(),
            device_type: Utils.getDeviceType(),
            uuid: Utils.getUuid(),
            user_id: DataProvider.getUserId(),
            core_id: DataProvider.getCoreId(),
            url_params: Utils.getUrlParams(),
            country: (await DataProvider.getCountry()) || null,
            cookies: Utils.getCookies(),
            gender: DataProvider.getGender(),
            ab_test_name: (await DataProvider.getAbTestName()) || 'default_ab_test_name',
            ab_test_split_name: (await DataProvider.getAbTestSplitName()) || null,
            growthbook_ab_test_name: DataProvider.getGrowthBookTestName(),
            region: DataProvider.getRegion(),
            ...(utmSource && { utm_source: utmSource }),
            ...(eventAction && { event_action: eventAction }),
            ...(userSource && { source: userSource }),
        };
    };

    public static errorConsoleGroup = (options: {
        systemName: SYSTEMS_NAMES | string;
        data?: object;
        error: unknown;
        event: string;
    }) => {
        console.error('Cant track analytic - ', options.systemName);
        console.error('With error - ', options.error);
        console.error('Data -', JSON.stringify(options.data));
        console.groupEnd();
        DataProvider.handleError(options.error);
    };

    public static addPrefix = (event: string, prefixStatus?: boolean) => {
        return prefixStatus ? `${event}__screen__load` : event;
    };

    public static getUTCDate = () => {
        const dateNow = new Date();

        return new Date(dateNow.getTime() + dateNow.getTimezoneOffset() * 60000);
    };

    public static formatOptanonGroups(groups?: string) {
        if (!groups) {
            return '';
        }

        const formattedGroups = groups
            .split(',')
            .filter((group) => group.trim() !== '')
            .sort()
            .join(', ');

        return formattedGroups;
    }
}
